import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react";
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import { InputForm } from "../InputForm/InputForm";
import DatePicker from "react-datepicker";
import { Button, Container } from "react-bootstrap";
import { Checkbox } from "../Checkbox/Checkbox";
import { Notification } from "../Notification/Notification";
import { MessageType } from "../Message/MessageType";
// import "./Message.css";
import "../NewMessage/NewUIMessage.css";
import { Modal } from "../../Components/Modal/Modal";
import {
  UpdateMessagesMutationVariables,
  useDeleteMessagesMutation,
  useGetMessagesByIdLazyQuery,
  useUpdateMessagesMutation,
} from "../../graphql/generated/graphql";
import { ToastContent } from "../Notification/types";
import Select from "react-select";
import {
  colorOptions,
  actionOptions,
  checkURLValid,
} from "../NewMessage/NewMessage";
import { BackButton } from "../_shared/Buttons/BackButton";
import { selectStyles } from "../EditPolicy/EditPolicyTypes";
import { audiences } from "../NewMessage/Constants";
import { Heading, SwitchField } from "@aws-amplify/ui-react";

const Delta = Quill.import("delta");

export const Message = () => {
  const [message, setMessage] = useState({} as MessageType);
  const { id } = useParams();
  const [startDate, setStartDate] = useState(new Date() as Date);
  const [endDate, setEndDate] = useState(new Date() as Date);
  const navigate = useNavigate();
  const timeout: number = 4000;
  const [showCTAFields, setShowCTAFields] = useState(false);
  const [isWebLink, setIsWebLink] = useState(false);
  const [showModal, setShowModal] = useState(false as boolean);
  const [showUpdateModal, setShowUpdateModal] = useState(false as boolean);
  const [someOptionsDisabled, setSomeOptionsDisabled] = useState(false);
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>([]);
  const [toast, setToast] = useState<ToastContent>({
    title: "",
    description: "",
    isVisible: false,
    notificationVariant: "success",
  });

  const quillRef = useRef<ReactQuill | null>(null);

  const [updateMessages] = useUpdateMessagesMutation();
  const [getMessagesById] = useGetMessagesByIdLazyQuery();
  const [deleteMessages] = useDeleteMessagesMutation();

  const goBack = () => {
    navigate(-1);
  };

  const modules = useMemo(
    () => ({
      toolbar: [
        [
          {
            font: [],
          },
        ],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "link"],
        [{ list: "ordered" }, { list: "bulleted" }],
        [{ indent: -1 }, { indent: 1 }, { align: [] }],
        [],
      ],
      clipboard: {
        // will add the matcher after mount to keep this minimal
      },
    }),
    []
  );

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const ELEMENT_NODE = 1;
    quill.clipboard.addMatcher(ELEMENT_NODE, (_node, delta) => {
      const ops = delta.ops
        .map((op) => {
          if (typeof op.insert === "string") {
            return { insert: op.insert };
          }
          return null;
        })
        .filter(Boolean);
      return new Delta(ops as any);
    });
  }, []);

  const handleStartDate = (value: Date) => {
    if (value) {
      setStartDate(value);
      const convertedStartDate = new Date(value);
      const startDateISOString = convertedStartDate.toISOString();
      setMessage((message: MessageType) => {
        return {
          ...message,
          StartDate: startDateISOString,
        };
      });
    }
  };

  const handleEndDate = (value: Date) => {
    setEndDate(value);
    const convertedEndDate = new Date(value);
    const endDateISOString = convertedEndDate.toISOString();
    setMessage((message: MessageType) => {
      return {
        ...message,
        EndDate: endDateISOString,
      };
    });
  };

  const update = async () => {
    const variables: UpdateMessagesMutationVariables = {
      messages: {
        id,
        Title: message.Title,
        Headline: message.Headline,
        Summary: message.Summary,
        Description: message.Description,
        StartDate: message.StartDate,
        EndDate: message.EndDate,
        Active: message.Active,
        TMCUsers: message.TMCUsers,
        TIUsers: message.TIUsers,
        Renewals: message.Renewals,
        GuestUsers: message.GuestUsers,
        LoggedInUsers: message.LoggedInUsers,
        AllUsers: message.AllUsers,
        Marketing: message.Marketing,
        MatchAll: message.MatchAll,
        HasCTAButton: message.HasCTAButton,
        IsWebLink: message.IsWebLink,
        WebLinkURL: message.WebLinkURL,
        ButtonLabel: message.ButtonLabel,
        CallToActionText: message.CallToActionText,
        CallToActionType: message.CallToActionType,
        CallToActionBtnColor: message.CallToActionBtnColor,
      },
    };
    const response = await updateMessages({ variables });
    console.log(`RESPONSE ${JSON.stringify(response)}`);
    setToast({
      title: "Message Edited",
      description: `Message with title: ${message?.Title} has been edited`,
      isVisible: true,
      notificationVariant: "success",
    });
    setShowUpdateModal(false);
  };

  const isURLValid = message?.WebLinkURL
    ? checkURLValid(message?.WebLinkURL)
    : true;

  const handleUpdateMessage = async () => {
    try {
      if (message.HasCTAButton) {
        if (message.IsWebLink) {
          if (
            message.ButtonLabel?.length === 0 ||
            message.CallToActionBtnColor?.length === 0 ||
            message.WebLinkURL?.length === 0 ||
            !isURLValid
          ) {
            setToast({
              title: "Error!",
              description:
                "Please ensure that all call-to-action fields are filled in, the weblink URL is valid and starts with http:// or https://",
              isVisible: true,
              notificationVariant: "danger",
            });
            return;
          } else {
            await update();
          }
        } else {
          if (
            message.ButtonLabel?.length === 0 ||
            message.CallToActionText?.length === 0 ||
            message.CallToActionType?.length === 0 ||
            message.CallToActionBtnColor?.length === 0
          ) {
            setToast({
              title: "Error!",
              description:
                "Please ensure that all call-to-action fields are filled in",
              isVisible: true,
              notificationVariant: "danger",
            });
            return;
          } else {
            await update();
          }
        }
      } else {
        await update();
      }
    } catch (e) {
      console.log(e);
      setToast({
        title: "Error!",
        description: `Failed to update message w/ error ${e.message}`,
        isVisible: true,
        notificationVariant: "danger",
      });
    }
  };

  const handleDeleteMessage = async (id: string) => {
    try {
      await deleteMessages({ variables: { messagesId: id } });
      setToast({
        title: "Success",
        description: `Successfully deleted message with title ${message?.Title}`,
        isVisible: true,
        notificationVariant: "success",
      });
      setShowModal(false);
      setTimeout(() => {
        navigate(-1);
      }, timeout);
    } catch (e) {
      console.log(e);
      setToast({
        title: "Error!",
        description: `Failed to delete message. ${e.message}`,
        isVisible: true,
        notificationVariant: "danger",
      });
      throw new Error("Something went wrong");
    }
  };

  const handleMessage = useCallback(async () => {
    try {
      const messageById = await getMessagesById({
        variables: { messagesId: id },
        fetchPolicy: "network-only",
      });
      const messageData = messageById?.data?.getMessagesById;
      setMessage(messageData);
      setStartDate(new Date(messageData?.StartDate));
      setEndDate(new Date(messageData?.EndDate));
      const preselectedAudiences = audiences
        .filter(({ name }) => messageData[name] === true)
        .map(({ name }) => name);

      setSelectedAudiences(preselectedAudiences);
    } catch (e) {
      console.log(e);
      setToast({
        title: "Error Fetching messages",
        description: `An error occured ${e} whilst fetching the messages`,
        isVisible: true,
        notificationVariant: "danger",
      });
    }
  }, [getMessagesById, id]);

  const disableAudienceOptions = useCallback(() => {
    if (
      message.CallToActionType === "referral" ||
      message.CallToActionType === "topup"
    ) {
      setSomeOptionsDisabled(true);
      audiences.forEach((audience) => {
        if (audience.name === "GuestUsers" || audience.name === "AllUsers") {
          const element = document.getElementsByName(
            audience.name
          )[0] as HTMLInputElement;
          if (element) {
            element.checked = false;
            element.disabled = true;
          }
        }
      });
      setMessage((message) => ({
        ...message,
        GuestUsers: false,
        AllUsers: false,
      }));
    } else {
      audiences.forEach((audience) => {
        if (audience.name === "GuestUsers" || audience.name === "AllUsers") {
          const element = document.getElementsByName(
            audience.name
          )[0] as HTMLInputElement;
          if (element) {
            element.disabled = false;
          }
        }
      });
    }
  }, [message.CallToActionType]);

  useEffect(() => {
    handleMessage();
  }, [handleMessage]);

  useEffect(() => {
    //auto disable audience options for certain action button types
    if (message?.CallToActionType) {
      disableAudienceOptions();
    }
  }, [disableAudienceOptions, message?.CallToActionType]);

  const handleChangeText = (value: string, name: string) => {
    const strippedValue = stripHTML(value);
    console.log("looking at stripped value" + strippedValue);
    setMessage((message) => {
      return {
        ...message,
        [name]: strippedValue,
      };
    });
  };

  const handleDescription = (value: string) => {
    setMessage((message) => {
      return {
        ...message,
        Description: value,
      };
    });
  };

  const handleSelectedEndDate = () => {
    if (startDate > endDate) {
      return startDate;
    } else {
      return endDate;
    }
  };

  const handleSelectChange = (selected) => {
    setMessage((message) => ({
      ...message,
      CallToActionText: selected.label,
      CallToActionType: selected.value,
    }));
    // if selected is referral or topup,
    // disable option for guest users and
    // all users from audience
    disableAudienceOptions();
  };

  const handleColorChange = (selected) => {
    setMessage((message) => ({
      ...message,
      CallToActionBtnColor: selected.value,
    }));
  };

  const handleCheckValue = (event: React.MouseEvent<HTMLInputElement>) => {
    const currentTarget = event.target as HTMLInputElement;
    const checked = currentTarget.checked;
    const name = currentTarget.name;
    if (name !== "Active") {
      setSelectedAudiences((prev) =>
        checked ? [...prev, name] : prev.filter((item) => item !== name)
      );
    }
    setMessage((message) => {
      return {
        ...message,
        [name]: checked,
      };
    });
  };

  const handleCheckCallToAction = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setShowCTAFields(!showCTAFields);
    setMessage((message) => {
      return {
        ...message,
        HasCTAButton: checked,
      };
    });
  };

  const handleCheckIsWebLink = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsWebLink(!isWebLink);
    setMessage((message) => {
      return {
        ...message,
        IsWebLink: checked,
      };
    });
  };

  const handleNotificationClose = () => {
    setToast({ ...toast, isVisible: false });
    navigate("/messages");
  };

  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleShowUpdateModal = () => {
    setShowUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
  };

  const stripHTML = (html) => {
    console.log("trying to strip");
    let dbClient = new DOMParser().parseFromString(html, "text/html");
    return dbClient.body.textContent || "";
  };

  const isAudienceSelected = selectedAudiences.length > 0;
  const disableButton = () => {
    if (
      message?.Title?.length === 0 ||
      message?.Headline?.length === 0 ||
      message?.StartDate?.length === 0 ||
      !isAudienceSelected
    ) {
      return true;
    } else {
      return false;
    }
  };

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/messages");
  };

  const toastVariant = toast.title === "Error!" ? "danger" : "success";

  return (
    <div>
      <Container fluid className="screenContainer message-page">
        <div className="card message-card">
          <BackButton onClick={onClick} label="Back to messages" />
          <Heading level={2} className="screen-title">
            Edit Message
          </Heading>
          <div className="formContainer">
            <div
              className={
                showCTAFields || message?.HasCTAButton
                  ? "editor-onshow"
                  : "editor-onhide"
              }
            >
              <div className="formItem">
                <InputForm
                  name="Title"
                  ariaLabel="Title"
                  size="lg"
                  inLineLabel
                  onChange={handleChangeText}
                  labelText="Title"
                  controlId="formMessages"
                  defaultValue={message?.Title}
                  required={true}
                  maxLength={100}
                />
              </div>
              <div className="formItem">
                <InputForm
                  name="Headline"
                  ariaLabel="Headline"
                  size="lg"
                  inLineLabel
                  onChange={handleChangeText}
                  labelText="Headline"
                  controlId="formMessages"
                  defaultValue={message?.Headline}
                  required={true}
                />
              </div>
              <div className="formItem">
                <InputForm
                  name="Summary"
                  ariaLabel="Summary"
                  size="lg"
                  inLineLabel
                  onChange={handleChangeText}
                  labelText="Summary"
                  controlId="formMessages"
                  defaultValue={message?.Summary}
                />
              </div>

              <div className="formItem field-row field-row--top">
                <div className="field-label desc">Description</div>
                <div className="field-control">
                  <ReactQuill
                    ref={quillRef}
                    modules={modules}
                    theme="snow"
                    className="rich-editor no-border"
                    value={message?.Description}
                    onChange={handleDescription}
                  />
                </div>
              </div>
              <div className="formItem field-row">
                <div className="field-label start">
                  Start Date <span className="required">*</span>
                </div>
                <div className="field-control">
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date) => handleStartDate(date)}
                    showTimeInput
                    showMonthPicker
                    showMonthDropdown
                    showYearDropdown
                    timeInputLabel="Time:"
                    dateFormat="dd/MM/yyyy h:mm aa"
                    className="date-input"
                  />
                </div>
              </div>

              <div className="formItem field-row">
                <div className="field-label end">End Date</div>
                <div className="field-control">
                  <DatePicker
                    selected={handleSelectedEndDate()}
                    onChange={(date: Date) => handleEndDate(date)}
                    minDate={startDate}
                    showTimeInput
                    showMonthPicker
                    showMonthDropdown
                    showYearDropdown
                    timeInputLabel="Time:"
                    dateFormat="dd/MM/yyyy h:mm aa"
                    className="date-input"
                  />
                </div>

                <div className="checkbox-item">
                  <Checkbox
                    label={"Is Active"}
                    name={"Active"}
                    className="action-checkbox"
                    handleCheck={handleCheckValue}
                    defaultChecked={message?.Active}
                  />
                </div>
              </div>

              <div className="formItem">
                <h3 className="audience-title">Action Button</h3>
                <div className="action-grid checkbox-item">
                  <Checkbox
                    label={"Has call-to-action button"}
                    name={"HasCTAButton"}
                    className="action-checkbox"
                    handleCheck={handleCheckCallToAction}
                    defaultChecked={message?.HasCTAButton}
                  />
                  {message?.HasCTAButton && (
                    <div className="inner-formItem">
                      <div className="inner-formItem field-row">
                        <p className="description-title">Is Weblink</p>
                        <SwitchField
                          name="subscribe-controlled"
                          label="Is Weblink"
                          isLabelHidden={true}
                          checked={isWebLink}
                          className="action-toggle"
                          defaultChecked={message?.IsWebLink}
                          onChange={handleCheckIsWebLink}
                        />
                      </div>
                      {message?.IsWebLink ? (
                        <div className="inner-formItem">
                          <InputForm
                            name="WebLinkURL"
                            ariaLabel="WebLinkURL"
                            size="lg"
                            inLineLabel
                            onChange={handleChangeText}
                            labelText="Web Link URL"
                            controlId="formMessages"
                            required={true}
                            onBlur={(e) => checkURLValid(e.target.value)}
                            defaultValue={message?.WebLinkURL}
                          />
                          {!isURLValid && message?.WebLinkURL && (
                            <p className="info-text-sentence">
                              URL is invalid. Please ensure it starts with
                              http://
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="inner-formItem field-row">
                          <p className="description-title">Button Action</p>
                          <Select
                            defaultValue={{
                              value: "",
                              label: "Please select a call-to-action",
                            }}
                            options={actionOptions}
                            styles={selectStyles}
                            className="select-feild"
                            onChange={handleSelectChange}
                          />
                        </div>
                      )}

                      <div className="inner-formItem field-row">
                        <p className="description-title">
                          Select Button Action
                        </p>
                        <Select
                          defaultValue={{
                            value: message?.CallToActionType,
                            label:
                              message?.CallToActionText ||
                              "Please select a call-to-action",
                          }}
                          options={actionOptions}
                          styles={selectStyles}
                          className="select-feild"
                          onChange={handleSelectChange}
                        />
                      </div>
                      <div className="inner-formItem field-row">
                        <p className="description-title">
                          Select Button Colour
                        </p>
                        <Select
                          defaultValue={colorOptions.find(
                            (o) => o.value === message?.CallToActionBtnColor
                          )}
                          options={colorOptions}
                          styles={selectStyles}
                          className="select-feild"
                          onChange={handleColorChange}
                          placeholder="Please select a colour"
                        />
                      </div>
                      <div className="inner-formItem">
                        <InputForm
                          name="ButtonLabel"
                          ariaLabel="Button Label"
                          size="lg"
                          inLineLabel
                          onChange={handleChangeText}
                          labelText="Button Label"
                          controlId="formMessages"
                          required={true}
                          maxLength={15}
                          defaultValue={message?.ButtonLabel}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="formItem">
                <h3 className="audience-title">Target Audience</h3>
                {someOptionsDisabled && (
                  <p className="hint spaced">
                    Some options are disabled for this action button as those
                    users have restricted access to selected feature
                  </p>
                )}
                <div className="audience-grid checkbox-list">
                  {audiences.map(({ label, name }) => (
                    <Checkbox
                      key={name}
                      label={label}
                      name={name}
                      handleCheck={handleCheckValue}
                      defaultChecked={message?.[name]}
                    />
                  ))}
                </div>
              </div>

              <div className="button-wrapper-new actions">
                <Button
                  variant="primary"
                  type="submit"
                  className="btn-save"
                  disabled={disableButton()}
                  onClick={handleShowUpdateModal}
                >
                  Save
                </Button>
                <Button
                  variant="danger"
                  type="button"
                  className="btn-delete"
                  onClick={() => handleShowModal()}
                >
                  Delete
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="tn-cancel"
                  onClick={goBack}
                >
                  Cancel
                </Button>
              </div>

              <div className="notification-wrapper">
                <Notification
                  show={toast.isVisible}
                  timer={timeout}
                  position="top-center"
                  title={toast.title}
                  description={toast.description}
                  autohide={true}
                  handleClose={handleNotificationClose}
                  variant={toastVariant}
                />
              </div>
            </div>
          </div>
          <Modal
            enableModal={showModal}
            title={"Delete Message"}
            closeButtonText={"Close"}
            actionButtonText={"Delete"}
            handleClose={handleCloseModal}
            handleSubmit={() => handleDeleteMessage(id)}
          >
            Are you sure you want to delete message with title: {message.Title}?
          </Modal>
          <Modal
            enableModal={showUpdateModal}
            title={"Edit Message"}
            closeButtonText={"Close"}
            actionButtonText={"Save"}
            handleClose={handleCloseUpdateModal}
            handleSubmit={() => handleUpdateMessage()}
          >
            Are you sure you want to edit message with title: {message.Title}?
          </Modal>
        </div>
      </Container>
    </div>
  );
};
